import { t as uiText } from '../../i18n';
export type CreditAlertKind = 'approaching' | 'exhausted' | 'grace';

export type CreditAlertItem = {
  kind: CreditAlertKind;
  message: string;
};

export function creditPeriodId(now: Date = new Date()): string {
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const year = jst.getUTCFullYear();
  const month = String(jst.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function jstDateKey(now: Date = new Date()): string {
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
}

export function creditAlertDismissKey(workspaceId: string, period: string, signature: string): string {
  return `notreu.creditAlert:${workspaceId}:${period}:${signature}:${jstDateKey()}`;
}

export function alertsFromUsageDoc(data: Record<string, unknown> | undefined | null): CreditAlertItem[] {
  if (!data) return [];
  const items: CreditAlertItem[] = [];
  const agent = typeof data.agentState === 'string' ? data.agentState : '';
  const internal = typeof data.internalRagState === 'string' ? data.internalRagState : '';
  const publicVisitor = typeof data.publicRagVisitor === 'string' ? data.publicRagVisitor : '';
  const internalState =
    internal === 'blocked' || agent === 'blocked'
      ? 'blocked'
      : internal === 'approaching' || agent === 'approaching'
        ? 'approaching'
        : '';

  if (internalState === 'approaching') {
    items.push({ kind: 'approaching', message: uiText("Internal AI quota is running low.") });
  } else if (internalState === 'blocked') {
    items.push({ kind: 'exhausted', message: uiText("This month's internal AI quota is used up.") });
  }

  if (publicVisitor === 'approaching') {
    items.push({ kind: 'approaching', message: uiText("Public AI quota is running low.") });
  } else if (publicVisitor === 'grace') {
    items.push({ kind: 'grace', message: uiText("Public questions are in a grace period. If you keep using them, they will stop soon.") });
  } else if (publicVisitor === 'stopped') {
    items.push({ kind: 'exhausted', message: uiText("Public questions are currently unavailable.") });
  }

  return items;
}

export type WelcomeBonusView = {
  amountCredits: number;
  remainingCredits: number;
  expiresAtMs: number;
  daysLeft: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** creditGrants/welcomeBonus の生データ → 表示用。期限切れ・不正値は null。 */
export function welcomeBonusView(
  data: Record<string, unknown> | undefined | null,
  nowMs: number = Date.now()
): WelcomeBonusView | null {
  if (!data) return null;
  const exp = data.expiresAt as { toMillis?: () => number } | undefined;
  const expiresAtMs = exp && typeof exp.toMillis === 'function' ? exp.toMillis() : null;
  const amountCredits = typeof data.amountCredits === 'number' ? data.amountCredits : 0;
  if (expiresAtMs === null || amountCredits <= 0 || nowMs >= expiresAtMs) return null;
  const consumedMilli = typeof data.consumedMilli === 'number' ? data.consumedMilli : 0;
  const remainingCredits = Math.max(0, amountCredits - consumedMilli / 1000);
  return {
    amountCredits,
    remainingCredits,
    expiresAtMs,
    daysLeft: Math.max(1, Math.ceil((expiresAtMs - nowMs) / DAY_MS)),
  };
}

/** 残り 20% 以下、または有効期限 7 日前から案内する（停止の警告とは別の穏やかな文面）。 */
export function alertsFromWelcomeBonus(view: WelcomeBonusView | null): CreditAlertItem[] {
  if (!view || view.remainingCredits <= 0) return [];
  const items: CreditAlertItem[] = [];
  if (view.remainingCredits <= view.amountCredits * 0.2) {
    items.push({
      kind: 'approaching',
      message: uiText("The welcome bonus credits are running low. The Starter plan gives you a larger monthly public AI quota."),
    });
  }
  if (view.daysLeft <= 7) {
    items.push({
      kind: 'approaching',
      message: uiText("The welcome bonus credits expire in {{days}} days. The Starter plan gives you a larger monthly public AI quota.", {
        days: view.daysLeft,
      }),
    });
  }
  return items;
}

export function alertSignature(items: CreditAlertItem[]): string {
  return items.map((item) => `${item.kind}:${item.message}`).join('|');
}
